import { AfterViewInit, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { SelectOption } from '../../interfaces/select-option';
import { EnumeratedTypesRepositoryService } from '../../services/enumerated-types/enumerated-types-repository.service';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { eMatSelectAppearance } from '../../enums';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators';
import { RegHubCommonConfig, REG_HUB_COMMON_CONFIG } from '../../reg-hub-common-config';
import { ValidationProblem } from '../../interfaces/validation/validation-problem';
import { Address } from '../../interfaces/address/address';
import { TranslocoService } from '@jsverse/transloco';

export interface JurisdictionSelectionLabels {
  search: string;
  noMatchesFound: string;
  federal: string;
  selectAll: string;
}

@Component({
    selector: 'lib-jurisdiction-selection',
    templateUrl: './jurisdiction-selection.component.html',
    styleUrls: ['./jurisdiction-selection.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class JurisdictionSelectionComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
    @Input() address: Address | null | undefined = null;
    @Input() countryFieldName?: string;
    @Input() jurisdictionFieldName?: string;

    // Some callers pass control-style names ('CountryCode', 'OrderJurisdictions'); show the translated label for those
    private static readonly CODE_LIKE_FIELD_NAMES = ['Country', 'CountryCode', 'OrderCountries', 'Jurisdiction', 'OrderJurisdictions'];
    get countryFieldLabel(): string {
        return this.countryFieldName && !JurisdictionSelectionComponent.CODE_LIKE_FIELD_NAMES.includes(this.countryFieldName)
            ? this.countryFieldName : this.transloco.translate('common.labels.country');
    }
    get jurisdictionFieldLabel(): string {
        return this.jurisdictionFieldName && !JurisdictionSelectionComponent.CODE_LIKE_FIELD_NAMES.includes(this.jurisdictionFieldName)
            ? this.jurisdictionFieldName : this.transloco.translate('common.labels.jurisdiction');
    }
    @Input() required: boolean = false;
    @Input() appearance: MatFormFieldAppearance = eMatSelectAppearance.fill;
    @Input() enabled: boolean = true;
    @Input() public defaultCountry: string | null = null;
    @Input() public defaultJurisdiction: string | null = null;
    @Input() countryFieldToFill: string = 'countryCode';
    @Input() jurisdictionFieldToFill: string = 'jurisdiction';
    @Input() multiSelect: boolean = false;
    @Input() allowInternationalAddressJurisdiction: boolean = false;
    @Input() includeFederal: boolean = false;
    @Input() autoCapitalizeFields: boolean = false;
    @Input() staticCountryCode?: string;
    @Input() staticJurisdiction?: string;
    private _labels?: Partial<JurisdictionSelectionLabels>;
    @Input() set labels(value: Partial<JurisdictionSelectionLabels> | null | undefined) { this._labels = value ?? undefined; }
    /** Caller-provided labels override the translated defaults. */
    get labels(): JurisdictionSelectionLabels {
      const defaults: JurisdictionSelectionLabels = {
        search: this.transloco.translate('addressForm.search'),
        noMatchesFound: this.transloco.translate('addressForm.noMatchesFound'),
        federal: this.transloco.translate('jurisdictions.CA._F'),
        selectAll: this.transloco.translate('multiJurisdictionSelect.selectAll'),
      };
      return { ...defaults, ...this._labels } as JurisdictionSelectionLabels;
    }
    @Input() labelTranslator?: (code: string, type: 'country' | 'jurisdiction', countryCode?: string) => string;
    @Input() langChange$?: Observable<string>;

    // Internal fixed control names - separate from display labels to avoid issues when language changes
    protected readonly COUNTRY_CONTROL = 'Country';
    protected readonly JURISDICTION_CONTROL = 'Jurisdiction';

    @Output() formReady = new EventEmitter<FormGroup>();
    @Output() jurisdictionChosenEvent = new EventEmitter<string | string[]>();
    @Output() countryChosenEvent = new EventEmitter<string>();

    countryControl = new FormControl();
    countryFilterControl = new FormControl();
    jurisdictionFilterControl = new FormControl();

    protected countries: SelectOption[] = [];
    protected jurisdictions: SelectOption[] = [];
    filteredCountries: BehaviorSubject<SelectOption[]> = new BehaviorSubject<SelectOption[]>(this.countries);
    filteredJurisdictions: BehaviorSubject<SelectOption[]> = new BehaviorSubject<SelectOption[]>(this.jurisdictions);

    countryError$: BehaviorSubject<string | null | undefined> = new BehaviorSubject<string | null | undefined>(null);
    jurisdictionError$: BehaviorSubject<string | null | undefined> = new BehaviorSubject<string | null | undefined>(null);

    public countrySelectionForm: FormGroup = this.formBuilder.group({});

    private selectedJurisdictions: string[] = [];
    public isAllSelected: boolean = false;

    protected shouldShowJurisdictionDropdown: boolean = true;
    protected isInitialized: boolean = false;
    private destroy$ = new Subject<void>();

    constructor(
        private repo: EnumeratedTypesRepositoryService,
        private formBuilder: FormBuilder,
        @Inject(REG_HUB_COMMON_CONFIG) private config: RegHubCommonConfig) { }

    ngOnInit(): void {
        this.isInitialized = false;
        this.repo.getCountryInfoAsSelectOptions(this.config.rootUrl)
            .subscribe(options => {
                this.countries = options;
                // Load the initial country list
                this.filteredCountries.next(this.countries.slice());

                const selectedCountry = this.staticCountryCode ?? this.address?.countryCode ?? this.defaultCountry;

                if (selectedCountry != null) {
                    this.setCountrySelection(selectedCountry);
                    this.countryChanged(selectedCountry);

                    if (this.staticCountryCode) {
                        this.countrySelectionForm.get(this.COUNTRY_CONTROL)?.disable({ emitEvent: false });
                    }

                    const selectedJurisdiction = this.staticJurisdiction ?? this.address?.jurisdiction ?? this.defaultJurisdiction;

                    if (selectedJurisdiction != null) {
                        this.setJurisdictionSelection(selectedJurisdiction);
                    }
                }
            });

        // Use fixed internal control names to avoid issues when language/labels change
        let formConfig: { [key: string]: any; } = {};
        if (this.required) {
            formConfig[this.COUNTRY_CONTROL] = [this.address?.countryCode ?? this.defaultCountry, Validators.required];
            formConfig[this.JURISDICTION_CONTROL] = [this.address?.jurisdiction ?? this.defaultJurisdiction, Validators.required];
        } else {
            formConfig[this.COUNTRY_CONTROL] = [this.address?.countryCode ?? this.defaultCountry, null];
            formConfig[this.JURISDICTION_CONTROL] = [this.address?.jurisdiction ?? this.defaultJurisdiction, null];
        }

        this.countrySelectionForm = this.formBuilder.group(formConfig);

        this.countrySelectionForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
            if (!value[this.COUNTRY_CONTROL]) {
                this.jurisdictions = [];
            }
        });

        this.formReady.emit(this.countrySelectionForm);

        if (!this.enabled) {
            this.countrySelectionForm.disable();
        }

        // Listen for search field value changes
        this.countryFilterControl.valueChanges.pipe(
                debounceTime(200),
                startWith(''),
                map(search => this.filterCountries(search)),
                takeUntil(this.destroy$)
            ).subscribe(filteredCountries => {
                this.filteredCountries.next(filteredCountries);

                if (this.staticCountryCode) {
                    this.countrySelectionForm.get(this.COUNTRY_CONTROL)?.disable({ emitEvent: false});
                }
            });

        this.jurisdictionFilterControl.valueChanges.pipe(
                debounceTime(200),
                startWith(''),
                map(search => this.filterJurisdictions(search)),
                takeUntil(this.destroy$))
            .subscribe(filteredJurisdictions => {
                this.filteredJurisdictions.next(filteredJurisdictions);
                this.setJurisdictionControlUsability(filteredJurisdictions);
            });

        // Re-sort when language changes
        (this.langChange$ ?? this.transloco.langChanges$).pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.sortAndUpdateOptions();
        });
    }

    ngAfterViewInit(): void {
        this.isInitialized = true;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private sortAndUpdateOptions(): void {
        // Re-sort countries by translated label
        this.countries = this.countries.sort((a, b) =>
            this.getDisplayLabel(a, 'country').toLowerCase()
                .localeCompare(this.getDisplayLabel(b, 'country').toLowerCase())
        );
        this.filteredCountries.next(this.filterCountries(this.countryFilterControl.value || ''));

        // Re-sort jurisdictions (excluding special options at the top)
        const specialValues = ['selectAll', '_F'];
        const specialOptions = this.jurisdictions.filter(j => specialValues.includes(j.value as string));
        const regularOptions = this.jurisdictions.filter(j => !specialValues.includes(j.value as string));

        regularOptions.sort((a, b) =>
            this.getDisplayLabel(a, 'jurisdiction').toLowerCase()
                .localeCompare(this.getDisplayLabel(b, 'jurisdiction').toLowerCase())
        );

        this.jurisdictions = [...specialOptions, ...regularOptions];
        this.filteredJurisdictions.next(this.filterJurisdictions(this.jurisdictionFilterControl.value || ''));
    }

    filterCountries(value: string): SelectOption[] {
        const filterValue = value.toLowerCase();
        return this.countries.filter(option =>
            this.getDisplayLabel(option, 'country').toLowerCase().includes(filterValue)
        );
    }

    filterJurisdictions(value: string): SelectOption[] {
        const filterValue = value.toLowerCase();
        return this.jurisdictions.filter(option =>
            this.getDisplayLabel(option, 'jurisdiction').toLowerCase().includes(filterValue)
        );
    }

    protected onCountryChange(event: MatSelectChange) {
        this.countryChanged(event.value);
        if (this.isInitialized) {
            this.countryChosenEvent.emit(event.value);
        }
    }

    protected onJurisdictionChange(event: MatSelectChange) {
        const selected = event.value;

        if (selected && selected.includes('selectAll')) {
            // If "Select All" is selected, select all jurisdictions
            this.isAllSelected = true;
            const allJurisdictions: string[] = this.jurisdictions
                .map(j => j.value)
                .filter((val): val is string => val !== 'selectAll' && val !== null);
            this.selectedJurisdictions = allJurisdictions;
            this.countrySelectionForm.get(this.JURISDICTION_CONTROL)?.setValue(allJurisdictions, { emitEvent: false });
        } else {
            // If "Select All" is deselected, or regular selections occur
            this.isAllSelected = false;
            this.selectedJurisdictions = selected;
        }

        if (this.isInitialized) {
            this.jurisdictionChosenEvent.emit(event.value);
        }
    }

    protected countryChanged(country: string) {
        const jurisdictionControl = this.countrySelectionForm.get(this.JURISDICTION_CONTROL);
        jurisdictionControl?.reset();

        this.repo.getJurisdictionInfoAsSelectOptions(this.config.rootUrl, country)
            .subscribe(options => {
                // Sort jurisdictions by translated label
                this.jurisdictions = options.sort((a, b) =>
                    this.getDisplayLabel(a, 'jurisdiction').toLowerCase()
                        .localeCompare(this.getDisplayLabel(b, 'jurisdiction').toLowerCase())
                );

                if (this.includeFederal) {
                    this.jurisdictions.unshift({ label: this.labels.federal, value: '_F' });
                }

                if (this.multiSelect) {
                    this.jurisdictions.unshift({ label: this.labels.selectAll, value: 'selectAll' });
                }

                this.filteredJurisdictions.next(this.jurisdictions.slice());

                this.setJurisdictionControlUsability(this.jurisdictions);
                this.setShouldShowJurisdictionDropdown();
            });
    }

    setCountrySelection(selectedCountry: string) {
        if (this.staticCountryCode) {
            selectedCountry = this.staticCountryCode;
        }

        const countryControl = this.countrySelectionForm.get(this.COUNTRY_CONTROL);
        const jurisdictionControl = this.countrySelectionForm.get(this.JURISDICTION_CONTROL);

        if (!this.staticCountryCode) {
            countryControl?.setValue(selectedCountry, { emitEvent: false });
        }

        if (!this.staticJurisdiction) {
            jurisdictionControl?.setValue(null, { emitEvent: false });
        }

        if (!this.staticCountryCode) {
            this.countryChanged(selectedCountry);
        }
    }

    setJurisdictionSelection(selectedJurisdiction: string) {
        if (this.staticJurisdiction) {
            selectedJurisdiction = this.staticJurisdiction;
        }

        const countryControl = this.countrySelectionForm.get(this.COUNTRY_CONTROL);

        if (countryControl) {
            this.countryChanged(countryControl.value);
            const jurisdictionControl = this.countrySelectionForm.get(this.JURISDICTION_CONTROL);

            jurisdictionControl?.setValue(selectedJurisdiction, { emitEvent: false });
        }

        if (!this.multiSelect) {
            this.selectedJurisdictions = [selectedJurisdiction];
        }

        return this.selectedJurisdictions;
    }

    setJurisdictionSelections(selectedJurisdictions: string[]) {
        const countryControl = this.countrySelectionForm.get(this.COUNTRY_CONTROL);

        if (countryControl) {
            this.countryChanged(countryControl.value);
            const jurisdictionControl = this.countrySelectionForm.get(this.JURISDICTION_CONTROL);

            jurisdictionControl?.setValue(selectedJurisdictions, { emitEvent: false });
        }

        return this.selectedJurisdictions = selectedJurisdictions;
    }

    private setJurisdictionControlUsability(jurisdictions: SelectOption[]) {
        this.setShouldShowJurisdictionDropdown();

        if (this.staticJurisdiction || (this.shouldShowJurisdictionDropdown && jurisdictions.length <= 1)) {
            this.countrySelectionForm.get(this.JURISDICTION_CONTROL)?.disable({emitEvent: false});
        } else {
            this.countrySelectionForm.get(this.JURISDICTION_CONTROL)?.enable({emitEvent: false});
        }
    }

    pushErrors(errors: ValidationProblem[] | undefined) {
        if (!errors) {
            return;
        }

        this.countryError$.next(errors?.filter(error => {
            var splitPath = error.path.split('/');
            return splitPath.at(splitPath.length - 1)?.includes(`/${this.countryFieldToFill}`);
        }).at(0)?.userFriendlyMessage);

        this.jurisdictionError$.next(errors?.filter(error => {
            var splitPath = error.path.split('/');
            return splitPath.at(splitPath.length - 1)?.includes(`/${this.jurisdictionFieldToFill}`);
        }).at(0)?.userFriendlyMessage);
    }

    public getSelectedJurisdictions(): string[] | string {
        return this.selectedJurisdictions;
    }

    public setShouldShowJurisdictionDropdown() {
        var selectedCountry = this.countrySelectionForm.get(this.COUNTRY_CONTROL)?.value;

        if (selectedCountry !== 'CA' && selectedCountry !== 'US' && this.allowInternationalAddressJurisdiction) {
            this.shouldShowJurisdictionDropdown = false;
        } else {
            this.shouldShowJurisdictionDropdown = true;
        }
    }

    protected clearCountry() {
        this.countrySelectionForm.get(this.COUNTRY_CONTROL)?.setValue(null, { emitEvent: false });
    }

    protected clearJurisdiction() {
        this.countrySelectionForm.get(this.JURISDICTION_CONTROL)?.setValue(null, { emitEvent: false });
    }

    getDisplayLabel(option: SelectOption, type: 'country' | 'jurisdiction'): string {
        if (!option.value) {
            return option.label;
        }
        const countryCode = type === 'jurisdiction'
            ? this.countrySelectionForm.get(this.COUNTRY_CONTROL)?.value
            : undefined;
        if (this.labelTranslator) {
            return this.labelTranslator(option.value as string, type, countryCode) || option.label;
        }
        if (option.value === '_F') {
            return this.labels.federal;
        }
        if (option.value === 'selectAll') {
            return this.labels.selectAll;
        }
        // Default: use the shared countries/jurisdictions translations, falling back to the API label
        const key = type === 'country' ? `countries.${option.value}` : `jurisdictions.${countryCode}.${option.value}`;
        const translated = this.transloco.translate(key);
        return translated && translated !== key ? translated : option.label;
    }
}
